'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useViewport } from '@/hooks';
import { WithLabel } from '../label/WithLabel';
import { SingleSelectOnDrawerUI } from './SingleSelectOnDrawerUI';
import { SingleSelectUI } from './SingleSelectUI';
import { type SingleSelectProps } from './type';

/**
 * 기본 단일 선택 컴포넌트
 *
 * @param value - 선택된 값 state
 * @param options - 선택 옵션 배열
 * @param onValueChange - 선택 값 변경 핸들러
 * @param placeholder - 선택 옵션이 없을 때 표시할 텍스트
 * @param error - 외부에서 주입되는 에러 여부
 * @param onError - 선택 옵션이 유효하지 않을 때 호출되는 핸들러
 * @param title - 선택 컴포넌트의 제목
 * @param titleLeftSlot - 선택 컴포넌트의 제목 왼쪽 슬롯
 * @param titleRightSlot - 선택 컴포넌트의 제목 오른쪽 슬롯
 * @param showScrollbar - 스크롤바 표시 여부
 * @param hasDrawerHandle - 드로어 핸들 표시 여부
 * @param valueRenderer - 선택된 값을 보여줄 렌더 함수
 * @param trigger - 선택 컴포넌트의 커스텀 트리거
 * @example
 * ```
 * <SingleSelect
 *    value={value}
 *    options={options}
 *    onValueChange={value => setValue(value)}
 * />
 * ```
 */

export const SingleSelect = <T extends string,>({
  required = true,
  ...props
}: SingleSelectProps<T>) => {
  const {
    isMobileSm
  } = useViewport();
  const SelectUIComp = isMobileSm ? SingleSelectOnDrawerUI : SingleSelectUI;
  const {
    isOpen,
    open,
    close,
    handleError,
    handleSelect,
    isError,
    renderedValue
  } = useSingleSelect(props);
  return <WithLabel title={props.title} titleLeftSlot={props.titleLeftSlot} titleRightSlot={props.titleRightSlot} description={props.description} helperText={props.helperText} optional={!required} error={isError} data-sentry-element="WithLabel" data-sentry-component="SingleSelect" data-sentry-source-file="SingleSelect.tsx">
      <SelectUIComp {...props} isOpen={isOpen} open={open} close={close} onError={handleError} onSelect={handleSelect} error={isError} renderedValue={renderedValue} data-sentry-element="SelectUIComp" data-sentry-source-file="SingleSelect.tsx" />
    </WithLabel>;
};

/**
 * 커스텀 Select UI에 인터랙션 로직을 추가하기 위한 hook
 *
 * 기본 Select UI 뿐만 아니라, dropdown, dialog, drawer 등 다양한 UI에 적용 가능
 *
 * @param props - 커스텀 Select UI 컴포넌트의 props
 */
export function useSingleSelect<T extends string>(props: SingleSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const {
    valueRenderer,
    options,
    value,
    onValueChange,
    onError
  } = props;
  const renderedValue = useMemo(() => {
    const selectedOption = options.find(option => option.value === value);
    if (!selectedOption) return undefined;
    return valueRenderer?.(selectedOption) ?? selectedOption.label;
  }, [options, value, valueRenderer]);
  const handleSelect = useCallback((v: T) => {
    if (v === value) {
      onValueChange(null);
    } else {
      onValueChange(v);
    }
    setIsOpen(false);
  }, [value, onValueChange]);
  useEffect(() => {
    const isInvalid = !!options.find(option => option.value === value)?.invalid;
    if (isInvalid) {
      onError?.();
    }
    setIsInvalid(isInvalid);
  }, [value, options, onError]);
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    handleError: () => setIsInvalid(true),
    handleSelect,
    isError: isInvalid || props.error,
    renderedValue
  };
}